import { DesktopIcon, MixIcon, PersonIcon } from '@radix-ui/react-icons';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { Box, Flex, Grid, Text } from '@radix-ui/themes';
import { MachineIcon } from 'components/common/custom-icon/shorthands';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonNavMenu } from 'components/common/layout/nav-menu';
import { SectionContent } from 'components/common/settings-dialog/content';
import { MachineContext } from 'contexts/machine.context';
import {
  MachineSettingsTabKey,
  SettingsSection,
  UserSettingsTabKey,
} from 'enums/settings';
import useLatest from 'hooks/useLatestRef';
import { t } from 'i18next';
import { IBaseDialog, IFullDialog } from 'interfaces/i-dialogs';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { SpecialMsPosition } from 'lib_ts/interfaces/machine-msg/i-special-mstarget';
import { ReactNode, useContext, useEffect, useState } from 'react';
import { useAuthStore } from 'stores/use-auth-store';

interface ISettingsSectionDef {
  label: string;
  value: SettingsSection;
  prefixIcon: ReactNode;
}

const ALL_SECTIONS: ISettingsSectionDef[] = [
  {
    label: 'common.machine',
    value: SettingsSection.Machine,
    prefixIcon: <MachineIcon />,
  },
  {
    label: 'settings.interface',
    value: SettingsSection.Interface,
    prefixIcon: <DesktopIcon />,
  },
  {
    label: 'settings.user',
    value: SettingsSection.User,
    prefixIcon: <PersonIcon />,
  },
  {
    label: 'settings.accessories',
    value: SettingsSection.Accessories,
    prefixIcon: <MixIcon />,
  },
];

export interface ISettingsDialog extends IBaseDialog {
  initialSection: SettingsSection;
  /** if provided, only tabs listed here will be rendered, length should be at least 1 if provided */
  sections?: SettingsSection[];

  initialMachineTab?: MachineSettingsTabKey;
  initialUserTab?: UserSettingsTabKey;
}

export const SettingsDialog = (props: ISettingsDialog) => {
  const authLoading = useAuthStore((s) => s.loading);

  const {
    loading: machineLoading,
    fetchMachine,
    specialMsTarget,
  } = useContext(MachineContext);

  const latestFetchMachine = useLatest(fetchMachine);
  const latestSpecialMsTarget = useLatest(specialMsTarget);

  // ensure the machine is lowered when the settings dialog is opened
  useEffect(() => {
    // re-fetch the latest details, in case changes were made out-of-band (e.g. via Admin Portal or another tab/user)
    latestFetchMachine.current();

    // safety precaution: ensure the machine is lowered when the settings dialog is opened (e.g. if triggered while auto-firing from Pitch List)
    // ? the call will automatically abandon if the user is not active
    latestSpecialMsTarget.current(SpecialMsPosition.lowered);
  }, []);

  const [sections] = useState(
    ALL_SECTIONS.filter(
      (s) => !props.sections || props.sections.includes(s.value)
    )
  );

  const [active, setActive] = useState(props.initialSection);

  const dialogProps: IFullDialog = {
    identifier: props.identifier,
    title: 'common.settings',
    width: RADIX.DIALOG.WIDTH.XL,
    vFlexHeight: RADIX.DIALOG.HEIGHT.LG,
    loading: machineLoading || authLoading,
    onClose: props.onClose,
    content: (
      <Grid columns="4" className={RADIX.VFLEX.WRAPPER} gap={RADIX.FLEX.GAP.MD}>
        <Box>
          <CommonNavMenu orientation="vertical">
            {sections.map((s, i) => (
              <NavigationMenu.Item key={i}>
                <NavigationMenu.Trigger
                  className={StringHelper.classNames([
                    'NavigationMenuTrigger',
                    active === s.value ? 'active' : 'inactive',
                  ])}
                  onClick={() => setActive(s.value)}
                >
                  <Flex
                    pt="2"
                    pb="2"
                    pl="3"
                    pr="3"
                    gap={RADIX.FLEX.GAP.SM}
                    align="center"
                  >
                    {s.prefixIcon}
                    <Text
                      size="2"
                      color={
                        active === s.value ? RADIX.COLOR.ACCENT : undefined
                      }
                    >
                      {t(s.label)}
                    </Text>
                  </Flex>
                </NavigationMenu.Trigger>
              </NavigationMenu.Item>
            ))}
          </CommonNavMenu>
        </Box>
        <Flex
          gridColumn="span 3"
          overflowX="hidden"
          overflowY="auto"
          className={RADIX.VFLEX.COLUMN}
          gap={RADIX.FLEX.GAP.MD}
          pr="2"
        >
          <Box className={RADIX.VFLEX.GROW}>
            <SectionContent {...props} active={active} />
          </Box>
        </Flex>
      </Grid>
    ),
  };

  return (
    <ErrorBoundary identifier="SettingsDialog">
      <CommonDialog {...dialogProps} />
    </ErrorBoundary>
  );
};
